import { defineStore } from 'pinia'
import { HttpStatusCode } from 'axios'
import { API } from '../../../services'
import { defaultPagination, defaultSorting } from '../../../services/defaults'
import type { Filters, Pagination } from '../../../services/types'
import { setPagination } from '../../../services/utils/utils'
import type { InventoryTransferListItem } from '../types'
import type { ListTransfersOptions } from '../services/transfers'
import router from '../../../router'

/** Bumps on each list fetch so stale tab responses do not overwrite newer results. */
let transfersFetchGeneration = 0

/** Invalidate in-flight list fetches (e.g. when active location is cleared). */
export function invalidateTransfersFetches() {
  transfersFetchGeneration++
}

type TransfersListState = {
  records: InventoryTransferListItem[]
  loading: boolean
  error: string | null
  pagination: Pagination
  sorting: { sortBy: string; sortingOrder: 'asc' | 'desc' | null }
  direction: ListTransfersOptions['direction'] | null
}

export const useTransfersStore = defineStore('inventoryTransfers', {
  state: (): TransfersListState => ({
    records: [],
    loading: false,
    error: null,
    pagination: { ...defaultPagination },
    sorting: { ...defaultSorting, sortBy: 'created_at', sortingOrder: 'desc' },
    direction: null,
  }),

  actions: {
    setPagination(pagination: Pagination) {
      this.pagination = setPagination(pagination)
    },

    setCurrentPage(addPage: boolean) {
      const page = this.pagination?.page ?? 1
      this.pagination = { ...this.pagination, page: addPage ? page + 1 : page - 1 }
    },

    dispatchNavigateToNextPage(addPage: boolean = false) {
      this.setCurrentPage(addPage)
    },

    setDirection(direction: ListTransfersOptions['direction'] | null) {
      this.direction = direction
    },

    async dispatchGetTransfers(filters?: Partial<Filters>, pagination?: Partial<Pagination>) {
      const generation = ++transfersFetchGeneration
      this.loading = true
      this.error = null
      try {
        const options: ListTransfersOptions | undefined = this.direction ? { direction: this.direction } : undefined
        const { status, data } = await API.transfers.listTransfers(filters, pagination ?? this.pagination, options)
        if (generation !== transfersFetchGeneration) {
          return
        }
        if (status !== HttpStatusCode.Ok) {
          this.records = []
          this.error = 'Unable to load transfers.'
          return
        }
        this.records = (data.data ?? []) as InventoryTransferListItem[]
        const meta =
          data.meta ??
          (data.current_page != null
            ? {
                current_page: data.current_page,
                per_page: data.per_page,
                total: data.total,
                last_page: data.last_page,
              }
            : undefined)
        if (meta) {
          this.setPagination({ meta: meta as any })
        }
      } catch (e: any) {
        if (generation !== transfersFetchGeneration) {
          return
        }
        this.records = []
        if (e?.response?.status === HttpStatusCode.Unauthorized) {
          await router.push('/login')
          return
        }
        const msg = e?.response?.data?.message
        this.error = typeof msg === 'string' && msg.trim() ? msg : 'Unable to load transfers. Please try again later.'
      } finally {
        if (generation === transfersFetchGeneration) {
          this.loading = false
        }
      }
    },
  },
})
